<template>
  <div class="welcome-container">
    <div class="animated-bg"></div>

    <div class="welcome-content">
      <!-- Hero Section -->
      <section class="hero-section glass-card">
        <div class="hero-badge">
          🚀 LIVE CASE STUDY
        </div>

        <h1 class="hero-title">
          <span class="brand-name">We Created a Viral YouTube Channel</span>
          <span class="tagline">Using our own AI platform to build content that actually connects</span>
        </h1>

        <div class="timeframe">In Just 3 Weeks</div>

        <!-- Stats Grid -->
        <div class="stats-grid">
          <div v-for="stat in viralStats" :key="stat.label" class="stat-card">
            <span class="stat-number">{{ stat.number }}</span>
            <div class="stat-label">{{ stat.label }}</div>
            <div class="stat-growth">{{ stat.growth }}</div>
          </div>
        </div>
      </section>

      <!-- How It Works Section -->
      <section class="how-it-works-section glass-card">
        <h2 class="section-title gradient-text">How Our AI Works</h2>
        <div class="how-it-works-grid">
          <div v-for="step in howItWorks" :key="step.title" class="step-card">
            <div class="step-icon">
              <v-icon :color="step.color" size="48">{{ step.icon }}</v-icon>
            </div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </div>
        </div>
      </section>

      <!-- Try It Section -->
      <section class="try-it-section glass-card">
        <h2 class="section-title gradient-text">Try It Yourself</h2>
        <div class="try-it-content">
          <div class="try-it-text">
            <h3>Ask Any Question - Get a Video in 10 Seconds</h3>
            <p>
              Our AI doesn't predict trends - it researches them in real-time. Ask any question about any topic,
              and watch as our system scours the web, analyzes the data, and generates a compelling video instantly.
            </p>

            <div class="example-queries">
              <div v-for="example in exampleQueries" :key="example.text" class="example-query">
                <v-icon :color="example.color">{{ example.icon }}</v-icon>
                <span>{{ example.text }}</span>
              </div>
            </div>

            <div class="free-offer">
              <v-icon color="success">mdi-gift</v-icon>
              <span><strong>3 Free Queries Daily</strong> - No credit card required</span>
            </div>
          </div>

          <div class="try-it-form">
            <v-btn
              color="primary"
              x-large
              block
              @click="tryPlatform"
              class="try-cta"
            >
              <v-icon left class="black--text">mdi-play-circle</v-icon>
              <span class="black--text">Try It Free Now</span>
            </v-btn>
            <p class="free-note">
              Start with 3 free queries daily - upgrade anytime for unlimited access
            </p>
          </div>
        </div>
      </section>

      <!-- Results Section -->
      <section class="results-section glass-card">
        <h2 class="section-title gradient-text">From Zero to Viral</h2>
        <div class="results-content">
          <p class="results-statement">
            We didn't just build another AI tool - we proved it works by using it ourselves.
            Our platform researches any topic you ask about, finds the compelling angles,
            and generates videos that actually connect with audiences.
          </p>

          <div class="results-points">
            <div v-for="point in resultsPoints" :key="point.text" class="results-point">
              <v-icon :color="point.color">{{ point.icon }}</v-icon>
              <span>{{ point.text }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Upgrade Options -->
      <section class="upgrade-section glass-card">
        <h2 class="section-title gradient-text">Ready for More?</h2>
        <div class="upgrade-options">
          <div class="upgrade-card" @click="openUpgradeDialog">
            <v-icon size="48" color="primary">mdi-rocket</v-icon>
            <h3>Upgrade to Pro</h3>
            <p>Unlimited queries, advanced features, priority support</p>
            <v-btn color="primary" outlined>
              <span>View Plans</span>
            </v-btn>
          </div>

          <div class="upgrade-card" @click="contactCollaboration">
            <v-icon size="48" color="secondary">mdi-handshake</v-icon>
            <h3>Collaboration Opportunities</h3>
            <p>Custom solutions, partnerships, enterprise deals</p>
            <v-btn color="secondary" outlined>
              <span>Let's Talk</span>
            </v-btn>
          </div>
        </div>
      </section>
    </div>

    <!-- Upgrade Dialog -->
    <v-dialog v-model="showUpgradeDialog" max-width="800">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="primary">mdi-star-outline</v-icon>
          Choose Your Plan
        </v-card-title>

        <v-card-text>
          <v-alert type="info" dense class="mb-4">
            Select the plan that fits your video creation needs. All plans include our core features with different usage limits.
          </v-alert>

          <div class="pricing-cards">
            <!-- Pro Plan -->
            <div class="pricing-card" :class="{ 'selected-plan': selectedPlan === 'pro' }" @click="selectPlan('pro')">
              <div class="pricing-header">
                <div class="plan-badge">POPULAR</div>
                <div class="plan-name">
                  <v-icon small>mdi-star-three-points-outline</v-icon>
                  <span>Pro</span>
                </div>
                <div class="plan-price">
                  <span class="price">$149</span>
                  <span class="period">/month</span>
                </div>
                <div class="plan-description">For professionals & creators</div>
              </div>

              <div class="plan-features">
                <div class="feature-item">
                  <v-icon small color="success">mdi-check</v-icon>
                  <span>3,000 AI-powered video queries</span>
                </div>
                <div class="feature-item highlight-feature">
                  <v-icon small color="success">mdi-check-circle</v-icon>
                  <span>Unlimited video segments</span>
                </div>
                <div class="feature-item">
                  <v-icon small color="success">mdi-check</v-icon>
                  <span>500 file & text submissions</span>
                </div>
                <div class="feature-item">
                  <v-icon small color="success">mdi-check</v-icon>
                  <span>Premium LLM access (1,500 queries)</span>
                </div>
                <div class="feature-item">
                  <v-icon small color="success">mdi-check</v-icon>
                  <span>Advanced video customization</span>
                </div>
                <div class="feature-item">
                  <v-icon small color="success">mdi-check</v-icon>
                  <span>Priority customer support</span>
                </div>
              </div>

              <div class="plan-action">
                <v-btn
                  color="primary"
                  block
                  @click.stop="selectPlan('pro')"
                >
                  <span class="black--text">{{ selectedPlan === 'pro' ? 'Selected' : 'Choose Pro' }}</span>
                </v-btn>
              </div>
            </div>

            <!-- Pro Team Plan -->
            <div class="pricing-card" :class="{ 'selected-plan': selectedPlan === 'team_pro' }" @click="selectPlan('team_pro')">
              <div class="pricing-header">
                <div class="plan-name">
                  <v-icon small>mdi-account-group</v-icon>
                  <span>Pro Team</span>
                </div>
                <div class="plan-price">
                  <span class="price">$399</span>
                  <span class="period">/month</span>
                </div>
                <div class="plan-description">For teams of 5 members</div>
              </div>

              <div class="plan-features">
                <div class="feature-item">
                  <v-icon small color="success">mdi-check</v-icon>
                  <span>Everything in Pro, plus:</span>
                </div>
                <div class="feature-item">
                  <v-icon small color="success">mdi-check</v-icon>
                  <span>Collaboration for 5 team members</span>
                </div>
                <div class="feature-item">
                  <v-icon small color="success">mdi-check</v-icon>
                  <span>6,000 AI-powered video queries</span>
                </div>
                <div class="feature-item">
                  <v-icon small color="success">mdi-check</v-icon>
                  <span>Premium LLM access (3,000 queries)</span>
                </div>
                <div class="feature-item">
                  <v-icon small color="success">mdi-check</v-icon>
                  <span>Advanced team analytics</span>
                </div>
                <div class="feature-item">
                  <v-icon small color="success">mdi-check</v-icon>
                  <span>Dedicated account manager</span>
                </div>
              </div>

              <div class="plan-action">
                <v-btn
                  outlined
                  block
                  :color="selectedPlan === 'team_pro' ? 'primary' : ''"
                  @click.stop="selectPlan('team_pro')"
                >
                  {{ selectedPlan === 'team_pro' ? 'Selected' : 'Choose Team' }}
                </v-btn>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="showUpgradeDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="navigateToEmailLogin"
            :disabled="!selectedPlan"
          >
            <span class="black--text">Login to Upgrade</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'ViralChannelLanding',

  data() {
    return {
      showUpgradeDialog: false,
      selectedPlan: 'pro',

      viralStats: [
        { number: '459.1K', label: 'Views', growth: '>999% growth' },
        { number: '3.6M', label: 'Impressions', growth: '>999% growth' },
        { number: '28.1K', label: 'Watch Hours', growth: '>999% growth' },
        { number: '8.0%', label: 'Click-Through Rate', growth: 'Above average' },
        { number: '+1.1K', label: 'New Subscribers', growth: '>999% growth' },
        { number: '3:40', label: 'Avg View Duration', growth: 'High engagement' }
      ],

      howItWorks: [
        {
          title: 'Ask Any Question',
          description: 'Type any question about any topic - from breaking news to niche interests.',
          icon: 'mdi-help-circle',
          color: 'primary'
        },
        {
          title: 'AI Researches Everything',
          description: 'Our system scours the web in real-time, finding the most compelling angles and data.',
          icon: 'mdi-web',
          color: 'secondary'
        },
        {
          title: 'Video Generated in 10 Seconds',
          description: 'Watch as your question becomes a polished, engaging video ready to share.',
          icon: 'mdi-video-plus',
          color: 'success'
        }
      ],

      exampleQueries: [
        {
          icon: 'mdi-trending-up',
          color: 'primary',
          text: '"What\'s driving Tesla\'s stock surge this week?"'
        },
        {
          icon: 'mdi-earth',
          color: 'secondary',
          text: '"How is climate change affecting coffee prices?"'
        },
        {
          icon: 'mdi-robot',
          color: 'success',
          text: '"Which jobs are most at risk from AI automation?"'
        },
        {
          icon: 'mdi-currency-usd',
          color: 'warning',
          text: '"Why are housing prices dropping in major cities?"'
        }
      ],

      resultsPoints: [
        {
          icon: 'mdi-lightning-bolt',
          color: 'primary',
          text: 'Real-time research - no outdated trend predictions'
        },
        {
          icon: 'mdi-target',
          color: 'secondary',
          text: 'Every video backed by fresh data and compelling narratives'
        },
        {
          icon: 'mdi-rocket-launch',
          color: 'success',
          text: 'From question to viral content in under 10 seconds'
        }
      ]
    };
  },

  methods: {
    tryPlatform() {
      // emit an event to trigger the try it functionality
      this.$emit('start-first-project');
    },

    openUpgradeDialog() {
      this.showUpgradeDialog = true;
    },

    selectPlan(plan) {
      this.selectedPlan = plan;
    },

    navigateToEmailLogin() {
      // redirect to login or upgrade process
      if (this.selectedPlan) {
        this.$router.push({ name: 'BasicLogin' });
      } else {
        this.$toast.error('Please select a plan to proceed.');
      }
    },

    contactCollaboration() {
      // Open email for collaboration inquiries
      const subject = encodeURIComponent('Collaboration Opportunities - Viral AI Platform');
      const body = encodeURIComponent(`Hi,

I'm interested in exploring collaboration opportunities with your AI video platform.

My organization/use case:

What I'm looking to achieve:

Scale/budget considerations:

Best way to contact me:

Let's discuss how we can work together!

Thanks`);

      window.location.href = `mailto:support@unstruct.ai?subject=${subject}&body=${body}`;
    }
  }
};
</script>

<style scoped>
.welcome-container {
  position: relative;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  color: white;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.animated-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  background:
    radial-gradient(circle at 20% 80%, rgba(15, 12, 41, 0.6) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(48, 43, 99, 0.6) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(36, 36, 62, 0.6) 0%, transparent 50%);
  animation: pulse 4s ease-in-out infinite alternate;
}

@keyframes pulse {
  0% { opacity: 0.8; }
  100% { opacity: 1; }
}

.welcome-content {
  position: relative;
  z-index: 1;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.glass-card {
  backdrop-filter: blur(20px);
  background: rgba(15, 12, 41, 0.7);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 3rem;
  padding: 3rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.glass-card:hover {
  background: rgba(15, 12, 41, 0.85);
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
}

/* Hero Section */
.hero-section {
  text-align: center;
  padding: 4rem 3rem;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 50px;
  padding: 0.75rem 1.5rem;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  font-weight: 500;
}

.hero-title {
  margin-bottom: 1.5rem;
}

.brand-name {
  display: block;
  font-size: 4rem;
  font-weight: 800;
  background: linear-gradient(135deg, #ff6b6b, #4ecdc4, #45b7d1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
  line-height: 1.1;
}

.tagline {
  display: block;
  font-size: 1.5rem;
  font-weight: 300;
  opacity: 0.9;
  margin-bottom: 1rem;
}

.timeframe {
  font-size: 2rem;
  color: #4ecdc4;
  font-weight: 600;
  margin-bottom: 3rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.stat-card {
  background: rgba(15, 12, 41, 0.6);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #ff6b6b, #4ecdc4, #45b7d1);
}

.stat-card:hover {
  background: rgba(15, 12, 41, 0.8);
  transform: translateY(-4px);
}

.stat-number {
  font-size: 3rem;
  font-weight: 800;
  color: #4ecdc4;
  display: block;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 1.1rem;
  font-weight: 500;
  opacity: 0.9;
  margin-bottom: 0.5rem;
}

.stat-growth {
  font-size: 0.9rem;
  color: #ff6b6b;
  font-weight: 600;
  background: rgba(255, 107, 107, 0.2);
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  display: inline-block;
}

/* Section Titles */
.section-title {
  font-size: 3rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 3rem;
}

.gradient-text {
  background: linear-gradient(135deg, #ff6b6b, #4ecdc4, #45b7d1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* How It Works Section */
.how-it-works-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.step-card {
  text-align: center;
  padding: 2rem;
  border-radius: 16px;
  background: rgba(15, 12, 41, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.step-card:hover {
  background: rgba(15, 12, 41, 0.6);
  transform: translateY(-4px);
}

.step-icon {
  margin-bottom: 1.5rem;
}

.step-card h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.step-card p {
  opacity: 0.85;
  line-height: 1.6;
}

/* Try It Section */
.try-it-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  align-items: center;
}

.try-it-text h3 {
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.try-it-text p {
  opacity: 0.9;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.example-queries {
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
}

.example-query {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(15, 12, 41, 0.4);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.example-query:hover {
  background: rgba(15, 12, 41, 0.6);
}

.free-offer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(76, 175, 80, 0.2);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 12px;
  font-weight: 500;
}

.try-it-form {
  text-align: center;
}

.try-cta {
  margin-bottom: 1rem;
}

.free-note {
  font-size: 0.85rem;
  opacity: 0.7;
  line-height: 1.4;
}

/* Results Section */
.results-statement {
  font-size: 1.5rem;
  font-weight: 500;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 3rem;
  opacity: 0.95;
}

.results-points {
  display: grid;
  gap: 1.5rem;
}

.results-point {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(15, 12, 41, 0.4);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.results-point:hover {
  background: rgba(15, 12, 41, 0.6);
  transform: translateX(4px);
}

.results-point span {
  font-size: 1.1rem;
  font-weight: 500;
}

/* Upgrade Section */
.upgrade-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.upgrade-card {
  text-align: center;
  padding: 3rem 2rem;
  border-radius: 16px;
  background: rgba(15, 12, 41, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.upgrade-card:hover {
  background: rgba(15, 12, 41, 0.6);
  transform: translateY(-4px);
}

.upgrade-card h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1rem 0;
}

.upgrade-card p {
  opacity: 0.85;
  line-height: 1.6;
  margin-bottom: 2rem;
}

/* Dialog Styles */
.pricing-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.pricing-card {
  flex: 1;
  min-width: 250px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.pricing-card:hover {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.selected-plan {
  border-color: #1976d2;
  box-shadow: 0 6px 12px rgba(25, 118, 210, 0.15);
}

.plan-badge {
  position: absolute;
  top: 10px;
  right: -28px;
  background-color: #1976d2;
  color: white;
  font-size: 10px;
  font-weight: bold;
  padding: 4px 30px;
  transform: rotate(45deg);
}

.pricing-header {
  margin-bottom: 20px;
  text-align: center;
}

.plan-name {
  font-weight: bold;
  font-size: 1.2rem;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.plan-price {
  margin-bottom: 10px;
}

.plan-price .price {
  font-size: 2rem;
  font-weight: bold;
  color: #1976d2;
}

.plan-price .period {
  font-size: 0.9rem;
}

.plan-description {
  font-size: 0.9rem;
  margin-bottom: 5px;
}

.plan-features {
  margin-bottom: 20px;
  flex-grow: 1;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.feature-item .v-icon {
  margin-right: 8px;
  margin-top: 2px;
}

.highlight-feature {
  color: #4caf50;
  font-weight: 500;
}

.plan-action {
  margin-top: auto;
}

/* Responsive Design */
@media (max-width: 768px) {
  .welcome-content {
    padding: 1rem;
  }

  .glass-card {
    padding: 2rem;
    margin-bottom: 2rem;
  }

  .hero-section {
    padding: 3rem 2rem;
  }

  .brand-name {
    font-size: 2.5rem;
  }

  .tagline {
    font-size: 1.25rem;
  }

  .timeframe {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .try-it-content {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .example-queries {
    grid-template-columns: 1fr;
  }

  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
  }

  .stat-number {
    font-size: 2.5rem;
  }

  .pricing-cards {
    flex-direction: column;
  }

  .pricing-card {
    width: 100%;
    margin-bottom: 16px;
  }
}

@media (max-width: 480px) {
  .brand-name {
    font-size: 2rem;
  }

  .tagline {
    font-size: 1rem;
  }

  .timeframe {
    font-size: 1.25rem;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .stat-number {
    font-size: 2rem;
  }
}
</style>
